import { getCachedAudioBlobUrl } from './slideAppAudioCache';

/** BGM 파일 경로 매핑용 (음원 추가 시 여기서 URL 반환) */
export type SlideBgmMoodId = 'calm' | 'cheerful' | 'tense' | 'melancholy';

export const SLIDE_BGM_MOOD_IDS: ReadonlyArray<SlideBgmMoodId> = [
  'calm',
  'cheerful',
  'tense',
  'melancholy',
];

export const SLIDE_BGM_TRACKS: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];

export type SlideBgmTrack = 1 | 2 | 3;

export function slideBgmTracksForMood(_moodId: SlideBgmMoodId): ReadonlyArray<SlideBgmTrack> {
  return SLIDE_BGM_TRACKS;
}

/**
 * Storage 오브젝트를 같은 파일명으로 교체했을 때 IndexedDB 무효화용.
 * 음원 내용을 바꾼 뒤에는 이 값만 올리면 됩니다 (예: v1 → v2).
 */
const BGM_CACHE_VERSION = 'v2';

function slideBgmFileName(moodId: SlideBgmMoodId, track: SlideBgmTrack): string {
  return `${moodId}-${track}.m4a`;
}

function slideBgmCacheKey(moodId: SlideBgmMoodId, track: SlideBgmTrack): string {
  return `slideBgm:${BGM_CACHE_VERSION}:${moodId}-${track}`;
}

export function slideBgmPublicPath(moodId: SlideBgmMoodId, track: SlideBgmTrack): string {
  return `/sounds/bgm/${slideBgmFileName(moodId, track)}`;
}

function slideBgmStorageUrl(moodId: SlideBgmMoodId, track: SlideBgmTrack): string | null {
  const base = (process.env.REACT_APP_SUPABASE_URL as string | undefined)?.trim().replace(/\/$/, '');
  if (!base) return null;
  return `${base}/storage/v1/object/public/app-public-assets/bgm/${slideBgmFileName(moodId, track)}`;
}

/**
 * 공개 음원 URL 후보.
 * 1) `REACT_APP_BGM_PUBLIC_BASE_URL` (선택 CDN)
 * 2) 버셀 `public/sounds/bgm`
 * 3) 예전 Supabase 폴백
 *
 * `sounds/bgm-source/{moodId}-{track}.mp3` → 빌드 시 `public/sounds/bgm/{moodId}-{track}.m4a`
 */
export function getSlideBgmCandidateUrls(moodId: SlideBgmMoodId, track: SlideBgmTrack): string[] {
  const file = slideBgmFileName(moodId, track);
  const list: string[] = [];
  const cdnBase = (process.env.REACT_APP_BGM_PUBLIC_BASE_URL as string | undefined)?.trim().replace(/\/$/, '');
  if (cdnBase) list.push(`${cdnBase}/${file}`);
  list.push(slideBgmPublicPath(moodId, track));
  const storage = slideBgmStorageUrl(moodId, track);
  if (storage) list.push(storage);
  return Array.from(new Set(list.filter(Boolean)));
}

export function resolveSlideBgmAudioSrc(moodId: SlideBgmMoodId, track: SlideBgmTrack): string | null {
  return getSlideBgmCandidateUrls(moodId, track)[0] ?? null;
}

async function getCachedSlideBgmBlobUrl(moodId: SlideBgmMoodId, track: SlideBgmTrack): Promise<string | null> {
  const cacheKey = slideBgmCacheKey(moodId, track);
  for (const src of getSlideBgmCandidateUrls(moodId, track)) {
    const playUrl = await getCachedAudioBlobUrl(src, cacheKey);
    if (playUrl) return playUrl;
  }
  return null;
}

/** 모달 오픈 시 등 — 네트워크는 miss일 때만, hit면 IDB만 */
export function primeSlideBgmTrack(moodId: SlideBgmMoodId, track: SlideBgmTrack): void {
  void getCachedSlideBgmBlobUrl(moodId, track);
}

/** 무드별 트랙 전부 백그라운드 워밍 (이미 IDB에 있으면 네트워크 없음) */
export function primeAllSlideBgmTracks(): void {
  for (const moodId of SLIDE_BGM_MOOD_IDS) {
    for (const track of slideBgmTracksForMood(moodId)) {
      primeSlideBgmTrack(moodId, track);
    }
  }
}

export type SlideBgmPlaybackHandle = {
  pause: () => void;
  resume: () => void;
  stop: () => void;
  isPaused: () => boolean;
  /** 재생/재개 시도 결과 (자동재생 차단 NotAllowedError 등이면 false). 기존 호출부는 사용하지 않아도 됨 */
  resumeAsync: () => Promise<boolean>;
  /** 최초 재생 시도 결과 */
  initialPlay: Promise<boolean>;
  /** 볼륨 0~1 (무음 루프는 무시) */
  setVolume: (v: number) => void;
};

function createSilentLoopHandle(): SlideBgmPlaybackHandle {
  const AnyWindow = window as unknown as { webkitAudioContext?: typeof AudioContext };
  const Ctor = window.AudioContext ?? AnyWindow.webkitAudioContext;
  if (!Ctor) {
    return {
      pause: () => {},
      resume: () => {},
      stop: () => {},
      isPaused: () => true,
      resumeAsync: () => Promise.resolve(true),
      initialPlay: Promise.resolve(true),
      setVolume: () => {},
    };
  }

  const ctx = new Ctor();
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const buffer = ctx.createBuffer(1, Math.max(2, Math.floor(ctx.sampleRate * 0.05)), ctx.sampleRate);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(gain);
  gain.connect(ctx.destination);
  source.start(0);

  return {
    pause: () => {
      void ctx.suspend();
    },
    resume: () => {
      void ctx.resume();
    },
    stop: () => {
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
      try {
        void ctx.close();
      } catch {
        /* ignore */
      }
    },
    isPaused: () => ctx.state === 'suspended',
    resumeAsync: () =>
      ctx
        .resume()
        .then(() => true)
        .catch(() => false),
    initialPlay: Promise.resolve(true),
    setVolume: () => {},
  };
}

function createAudioLoopHandle(playUrl: string): SlideBgmPlaybackHandle {
  const audio = new Audio();
  audio.preload = 'auto';
  audio.loop = true;
  audio.src = playUrl;
  const playAsync = (): Promise<boolean> => {
    try {
      const p = audio.play();
      if (!p || typeof p.then !== 'function') return Promise.resolve(!audio.paused);
      return p.then(() => true).catch(() => false);
    } catch {
      return Promise.resolve(false);
    }
  };
  const tryPlay = () => void playAsync();
  const initialPlay = playAsync();
  return {
    pause: () => {
      void audio.pause();
    },
    resume: tryPlay,
    stop: () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    },
    isPaused: () => audio.paused,
    resumeAsync: playAsync,
    initialPlay,
    setVolume: (v: number) => {
      audio.volume = Math.max(0, Math.min(1, v));
    },
  };
}

/**
 * `src`가 있으면 IndexedDB(+HTTP 캐시)에서 Blob URL을 확보한 뒤 `<audio loop>` 재생.
 * 같은 디바이스·같은 캐시 버전이면 재다운로드 없이 재생합니다.
 * 없으면 무음 루프(Web Audio).
 */
export async function startSlideBgmPlaybackAsync(
  src: string | null,
  opts?: { moodId: SlideBgmMoodId; track: SlideBgmTrack }
): Promise<SlideBgmPlaybackHandle> {
  if (opts) {
    const playUrl = await getCachedSlideBgmBlobUrl(opts.moodId, opts.track);
    if (!playUrl) return createSilentLoopHandle();
    return createAudioLoopHandle(playUrl);
  }
  if (src) {
    const playUrl = await getCachedAudioBlobUrl(src, `slideBgm:${BGM_CACHE_VERSION}:${src}`);
    if (!playUrl) return createSilentLoopHandle();
    return createAudioLoopHandle(playUrl);
  }
  return createSilentLoopHandle();
}
